import { Entity, Webhook } from '@square/ignition';
import {
  CodeExampleLanguage,
  codeExampleLanguages,
} from '@squareup/dex-types-oas';
import {
  CodeBlock,
  CodeBlockButtonActionGroup,
  CodeCloseButton,
  CodeCopyButton,
  CodeDropdownButton,
  CodeDropdownButtonItem,
  CodeExpandButton,
} from '@squareup/dex-ui';
import { Box, Paragraph10 } from '@squareup/dex-ui-shared-base';
import { useDecision } from '@squareup/dex-ui-shared-feature-detection';
import { ModalWideContent } from '@squareup/dex-ui-shared-market';
import { HighlightLanguage } from '@squareup/dex-utils-highlight';
import { codeLanguageDisplayMap } from '@squareup/dex-utils-oas';
import clsx from 'clsx';
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useAttributeTooltips } from './reference-code-block-hooks';
import styles from './reference-code-block.module.css';

interface ReferenceCodeBlockProps {
  title: ReactNode;
  code: string;
  currentLanguage?: HighlightLanguage;
  onLanguageChanged?: (language: CodeExampleLanguage) => void;
  isCodeLangLimited?: boolean;
  isRequest?: boolean | undefined;
  isLoading?: boolean | undefined;
  isLanguageLoading?: boolean;
  addCommentTypingAnimation?: boolean | undefined;
  fillParentHeight?: boolean | undefined;
  enableAttributeTooltip?: boolean | undefined;
  entity?: Entity | Webhook | undefined;
  /**
   * Initial code shows up before the actual code, and is not included in the copy button
   * code
   */
  initialCode?: string | undefined;
}

const ReferenceCodeBlock: FunctionComponent<ReferenceCodeBlockProps> = ({
  title,
  code,
  currentLanguage,
  onLanguageChanged,
  isRequest,
  isLoading,
  isLanguageLoading,
  addCommentTypingAnimation,
  fillParentHeight = false,
  isCodeLangLimited = false,
  enableAttributeTooltip = false,
  entity,
  initialCode,
}) => {
  const {
    decision: { enabled: fernGoExamples },
  } = useDecision('fern_go_examples');

  const [showModal, setShowModal] = useState(false);

  const { renderAttributeTooltip } = useAttributeTooltips({
    code,
    showModal,
    isLoading,
    codeBlockEntity: entity,
    enabled: enableAttributeTooltip,
  });

  const codeWithInitialContent = useMemo(() => {
    if (initialCode) {
      return `${initialCode}\n\n${code}`;
    }

    return code;
  }, [code, initialCode]);

  const displayedLanguageMemo = useMemo(() => {
    if (!currentLanguage) {
      return '';
    }

    if (isCodeLangLimited) {
      return codeLanguageDisplayMap.get('curl');
    }

    return (
      codeLanguageDisplayMap.get(currentLanguage as CodeExampleLanguage) || ''
    );
  }, [currentLanguage, isCodeLangLimited]);

  const codeLanguageMemo: HighlightLanguage = useMemo(() => {
    return currentLanguage || 'json';
  }, [currentLanguage]);

  const languagesMemo = useMemo(() => {
    return codeExampleLanguages
      .filter((lang) => {
        if (isCodeLangLimited) {
          return lang === 'curl';
        }
        return lang;
      })
      .filter((lang) => {
        // filter out go examples if not released
        if (!fernGoExamples) {
          return lang !== 'go';
        }
        return lang;
      })
      .map((lang) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        name: codeLanguageDisplayMap.get(lang)!,
        value: lang,
      }));
  }, [fernGoExamples, isCodeLangLimited]);

  const onLanguageChangedCallback = useCallback(
    (item: CodeDropdownButtonItem<{ value: CodeExampleLanguage }>) => {
      onLanguageChanged?.(item.value);
    },
    [onLanguageChanged]
  );

  function closeModal() {
    setShowModal(false);
  }
  return (
    <>
      <CodeBlock
        title={title}
        code={codeWithInitialContent}
        showLineNumbers={true}
        isRequest={isRequest}
        addCommentTypingAnimation={addCommentTypingAnimation}
        language={codeLanguageMemo}
        wrap={true}
        isLoading={isLoading}
        fillParentHeight={fillParentHeight}
        className={clsx(styles['hoverable-attrs'])}
        actionButtonGroup={
          <CodeBlockButtonActionGroup>
            {displayedLanguageMemo && languagesMemo.length > 1 && (
              <CodeDropdownButton
                selected={displayedLanguageMemo}
                onSelected={onLanguageChangedCallback}
                items={languagesMemo}
                isLoading={isLanguageLoading}
              ></CodeDropdownButton>
            )}
            {languagesMemo.length === 1 && (
              <Box padding={{ horizontal: '1x' }}>
                <Paragraph10 weight="semi-bold" margin={{ right: '0.5x' }}>
                  {displayedLanguageMemo}
                </Paragraph10>
              </Box>
            )}

            <CodeCopyButton code={code}></CodeCopyButton>
            <CodeExpandButton
              onClick={() => {
                setShowModal(!showModal);
              }}
            ></CodeExpandButton>
          </CodeBlockButtonActionGroup>
        }
      />
      {showModal && (
        <ModalWideContent
          noVerticalPadding={true}
          transparentBg={true}
          paddedSides={true}
          onMarketDialogDismissed={closeModal}
          className={clsx(styles['code-modal'])}
        >
          <CodeBlock
            title={title}
            code={codeWithInitialContent}
            showLineNumbers={true}
            isRequest={isRequest}
            addCommentTypingAnimation={addCommentTypingAnimation}
            language={codeLanguageMemo}
            className={clsx(
              styles['modal-code-block'],
              styles['hoverable-attrs']
            )}
            wrap={true}
            actionButtonGroup={
              <CodeBlockButtonActionGroup>
                {displayedLanguageMemo && (
                  <CodeDropdownButton
                    selected={displayedLanguageMemo}
                    onSelected={onLanguageChangedCallback}
                    items={languagesMemo}
                  ></CodeDropdownButton>
                )}
                <CodeCopyButton code={code}></CodeCopyButton>
                <CodeCloseButton onClick={closeModal} />
              </CodeBlockButtonActionGroup>
            }
          />
          {/* Render attributes within modal content so the attribute
              popovers are in front of the modal content */}
          {renderAttributeTooltip()}
        </ModalWideContent>
      )}
      {!showModal && renderAttributeTooltip()}
    </>
  );
};

export { ReferenceCodeBlock };
